"use client"

import Image from "next/image"
import { useCallback, useEffect, useRef, useState } from "react"
import {
  MEDIA_FALLBACK_COPY,
  MediaAspectSkeleton,
  MediaGridUnavailable,
} from "@/components/media/media-fallback"
import { cn } from "@/lib/utils"
import { IMAGE_QUALITY_CARD_THUMB } from "@/lib/media/image-presets"
import {
  appendCacheBustToImageUrl,
  stableImageUrlIdentity,
  stripImageCacheBustParam,
} from "@/lib/media/cloudflare-urls"

type Phase = "loading" | "ok" | "err"

/**
 * Album / memories grid cell: skeleton while loading, graceful unavailable on error.
 * Avoids broken-image icons and layout collapse.
 */
export function GalleryMediaImage({
  src,
  alt = "",
  className,
  sizes,
  quality,
  unoptimized,
  priority,
  roundedClass = "rounded-[inherit]",
}: {
  src: string
  alt?: string
  className?: string
  sizes: string
  quality?: number
  unoptimized?: boolean
  priority?: boolean
  /** Match parent clip, e.g. `rounded-2xl` */
  roundedClass?: string
}) {
  const [phase, setPhase] = useState<Phase>("loading")
  const [displaySrc, setDisplaySrc] = useState(src)
  const stableRef = useRef(stableImageUrlIdentity(src))
  const retryRef = useRef(0)

  // Depend only on `src`: internal cache-bust updates to `displaySrc` must not re-run this
  // (would reset retries and undo the bust — see EventCoverHeroStage).
  useEffect(() => {
    setDisplaySrc(src)
    retryRef.current = 0
    const nextStable = stableImageUrlIdentity(src)
    if (nextStable && nextStable === stableRef.current) {
      return
    }
    stableRef.current = nextStable
    setPhase("loading")
  }, [src])

  const onLoad = useCallback(() => {
    stableRef.current = stableImageUrlIdentity(displaySrc)
    retryRef.current = 0
    setPhase("ok")
  }, [displaySrc])

  const onError = useCallback(() => {
    if (
      retryRef.current < 2 &&
      displaySrc &&
      !displaySrc.startsWith("blob:") &&
      !displaySrc.startsWith("data:") &&
      !displaySrc.startsWith("/")
    ) {
      retryRef.current += 1
      setDisplaySrc((prev) =>
        appendCacheBustToImageUrl(stripImageCacheBustParam(prev), Date.now()),
      )
      setPhase("loading")
      return
    }
    setPhase("err")
  }, [displaySrc])

  const deferImageFade = !priority && phase === "loading"

  return (
    <span className="relative block h-full w-full min-h-0 overflow-hidden">
      {phase !== "err" ? (
        <Image
          key={stableImageUrlIdentity(displaySrc)}
          src={displaySrc}
          alt={alt}
          fill
          sizes={sizes}
          quality={quality}
          unoptimized={unoptimized}
          priority={priority}
          fetchPriority={priority ? "high" : "auto"}
          onLoad={onLoad}
          onError={onError}
          className={cn(
            className,
            "transition-opacity duration-300 ease-out",
            deferImageFade && "opacity-0",
            !deferImageFade && "opacity-100",
          )}
        />
      ) : null}
      {deferImageFade ? (
        <MediaAspectSkeleton className={cn("z-[1]", roundedClass)} />
      ) : null}
      {phase === "err" ? (
        <div className={cn("absolute inset-0 z-[2] overflow-hidden", roundedClass)}>
          <MediaGridUnavailable className="h-full min-h-full" />
        </div>
      ) : null}
    </span>
  )
}

/**
 * Masonry layout: intrinsic-height `<img>` with same loading / error behavior.
 */
export function GalleryMasonryImage({
  src,
  alt = "",
  className,
}: {
  src: string
  alt?: string
  className?: string
}) {
  const [phase, setPhase] = useState<Phase>("loading")
  const [displaySrc, setDisplaySrc] = useState(src)
  const stableRef = useRef(stableImageUrlIdentity(src))
  const retryRef = useRef(0)

  useEffect(() => {
    setDisplaySrc(src)
    retryRef.current = 0
    const nextStable = stableImageUrlIdentity(src)
    if (nextStable && nextStable === stableRef.current) {
      return
    }
    stableRef.current = nextStable
    setPhase("loading")
  }, [src])

  return (
    <div className="relative block w-full min-h-[8rem]">
      {phase !== "err" ? (
        // eslint-disable-next-line @next/next/no-img-element -- masonry heights are intrinsic
        <img
          key={stableImageUrlIdentity(displaySrc)}
          src={displaySrc}
          alt={alt}
          className={cn(
            className,
            "relative z-[2] block h-auto w-full transition-opacity duration-300",
            phase === "loading" && "opacity-0",
            phase === "ok" && "opacity-100",
          )}
          onLoad={() => {
            stableRef.current = stableImageUrlIdentity(displaySrc)
            retryRef.current = 0
            setPhase("ok")
          }}
          onError={() => {
            if (
              retryRef.current < 2 &&
              displaySrc &&
              !displaySrc.startsWith("blob:") &&
              !displaySrc.startsWith("data:") &&
              !displaySrc.startsWith("/")
            ) {
              retryRef.current += 1
              setDisplaySrc((prev) =>
                appendCacheBustToImageUrl(stripImageCacheBustParam(prev), Date.now()),
              )
              setPhase("loading")
              return
            }
            setPhase("err")
          }}
        />
      ) : null}
      {phase === "loading" ? (
        <div className="absolute inset-0 z-[1] min-h-[8rem]">
          <MediaAspectSkeleton className="h-full rounded-none" shimmer />
        </div>
      ) : null}
      {phase === "err" ? (
        <div className="absolute inset-0 z-[3] min-h-[8rem]">
          <MediaGridUnavailable className="h-full min-h-[8rem]" />
        </div>
      ) : null}
    </div>
  )
}

/** Event list row thumbnail — compact fallback, no broken icon */
export function EventCardThumbnail({
  src,
  alt,
  sizes,
  priority,
}: {
  src: string
  alt: string
  sizes: string
  priority?: boolean
}) {
  const [phase, setPhase] = useState<Phase>("loading")
  const [displaySrc, setDisplaySrc] = useState(src)
  const stableRef = useRef(stableImageUrlIdentity(src))
  const retryRef = useRef(0)

  useEffect(() => {
    setDisplaySrc(src)
    retryRef.current = 0
    const nextStable = stableImageUrlIdentity(src)
    if (nextStable && nextStable === stableRef.current) {
      return
    }
    stableRef.current = nextStable
    setPhase("loading")
  }, [src])

  if (phase === "err") {
    return (
      <MediaGridUnavailable
        className="absolute inset-0"
        label={MEDIA_FALLBACK_COPY.short}
        iconClassName="h-5 w-5"
      />
    )
  }

  const deferImageFade = !priority && phase === "loading"

  return (
    <>
      {deferImageFade ? (
        <MediaAspectSkeleton className="z-[1] rounded-[inherit]" shimmer={false} />
      ) : null}
      <Image
        key={stableImageUrlIdentity(displaySrc)}
        src={displaySrc}
        alt={alt}
        fill
        priority={priority}
        fetchPriority={priority ? "high" : "auto"}
        quality={IMAGE_QUALITY_CARD_THUMB}
        className={cn(
          "object-cover transition-transform duration-500 group-hover:scale-110",
          deferImageFade && "opacity-0",
          !deferImageFade && "opacity-100",
        )}
        sizes={sizes}
        onLoad={() => {
          stableRef.current = stableImageUrlIdentity(displaySrc)
          retryRef.current = 0
          setPhase("ok")
        }}
        onError={() => {
          if (
            retryRef.current < 2 &&
            displaySrc &&
            !displaySrc.startsWith("blob:") &&
            !displaySrc.startsWith("data:") &&
            !displaySrc.startsWith("/")
          ) {
            retryRef.current += 1
            setDisplaySrc((prev) =>
              appendCacheBustToImageUrl(stripImageCacheBustParam(prev), Date.now()),
            )
            setPhase("loading")
            return
          }
          setPhase("err")
        }}
      />
    </>
  )
}
